import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { Card, CardContent } from "../components/ui/card"
import { Badge } from "../components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs"
import { 
  Smartphone, 
  Monitor, 
  Tablet,
  Wifi,
  Settings,
  Menu,
  Search,
  Bell,
  User,
  Building2
} from "lucide-react"
import { BankingDashboard } from './banking-dashboard'
import { AICategorization } from './ai-categorization'
import { MultiCurrencyConverter } from './multi-currency-converter'
import { CurrencyDashboard } from './currency-dashboard'
import { PaymentProcessorManager } from './payment-processor-manager'
import { AdvancedAnalytics } from './advanced-analytics'
import { BankConnectionManager } from './bank-connection-manager'
import { MobileMoneyManager } from './mobile-money-manager'
import { BankManagement } from './bank-management'
import { bankingApi, BankAccount, BankTransaction } from '@/lib/api/banking'
import { useAuth } from '@/contexts/auth-context'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from './ui/dialog'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip'
import { Plus, CheckCircle2, RefreshCw, Sparkles, Loader2, AlertCircle, Filter, X, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Info, Edit, Trash2 } from 'lucide-react'
import { MoonLoader } from './ui/moon-loader'
import { useToast } from '../hooks/use-toast'
import { TranslatedText } from '../components/translated-text'
import { useTranslation } from '../hooks/useTranslation'

interface ResponsiveBankingLayoutProps {
  companyId?: string
  accountsRefreshKey?: number
  setAccountsRefreshKey?: (fn: (k: number) => number) => void
  isAddTransactionOpen?: boolean
  setIsAddTransactionOpen?: (open: boolean) => void
  transactionForm?: any
  setTransactionForm?: (fn: (prev: any) => any) => void
  transactionFormErrors?: Record<string, string>
  setTransactionFormErrors?: (errors: Record<string, string>) => void
  handleTransactionFormChange?: (field: string, value: string) => void
  handleCreateTransaction?: () => void
}

export function ResponsiveBankingLayout({ 
  companyId,
  accountsRefreshKey,
  setAccountsRefreshKey,
  isAddTransactionOpen,
  setIsAddTransactionOpen,
  transactionForm,
  setTransactionForm,
  transactionFormErrors,
  setTransactionFormErrors,
  handleTransactionFormChange,
  handleCreateTransaction
}: ResponsiveBankingLayoutProps) {
  const { toast } = useToast()
  const { tSync } = useTranslation()
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const [deviceType, setDeviceType] = useState<'mobile' | 'tablet' | 'desktop'>('desktop')
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('dashboard')
  const [batteryLevel, setBatteryLevel] = useState(85)
  const [signalStrength, setSignalStrength] = useState(4)
  const [manualViewOverride, setManualViewOverride] = useState<'mobile' | 'tablet' | 'desktop' | null>(null)

  // Monitor authentication state changes
  useEffect(() => {
    // Auth state updated
  }, [isAuthenticated, authLoading, companyId])

  useEffect(() => {
    const checkDeviceType = () => {
      if (manualViewOverride) {
        setDeviceType(manualViewOverride)
        return
      }
      
      const width = window.innerWidth
      if (width < 768) {
        setDeviceType('mobile')
      } else if (width < 1024) {
        setDeviceType('tablet')
      } else {
        setDeviceType('desktop')
      }
    }

    checkDeviceType()
    window.addEventListener('resize', checkDeviceType)

    return () => {
      window.removeEventListener('resize', checkDeviceType)
    }
  }, [manualViewOverride])


  const getSignalIcon = () => {
    const bars = Math.floor(signalStrength)
    return (
      <div className="flex items-center gap-1">
        {[...Array(4)].map((_, i) => (
          <div
            key={i}
            className={`w-1 h-${i < bars ? '3' : '1'} bg-green-500 rounded-sm`}
          />
        ))}
      </div>
    )
  }

  const getBatteryIcon = () => {
    return (
      <div className="flex items-center gap-1">
        <div className="w-6 h-3 border border-gray-400 rounded-sm relative">
          <div
            className="bg-green-500 h-full rounded-sm"
            style={{ width: `${batteryLevel}%` }}
          />
        </div>
        <div className="w-1 h-2 bg-gray-400 rounded-r-sm"></div>
      </div>
    )
  }

  const mobileTabs = [
    { id: 'dashboard', label: tSync('Dashboard'), icon: '📊' },
    { id: 'transactions', label: tSync('Transactions'), icon: '💳' },
    // { id: 'ai', label: 'AI', icon: '🤖' }, // Hidden for now
    { id: 'currency', label: tSync('Currency'), icon: '💱' },
    { id: 'payments', label: tSync('Payments'), icon: '💸' },
    { id: 'analytics', label: tSync('Analytics'), icon: '📈' },
    // { id: 'connections', label: 'Banks', icon: '🏦' }, // Hidden for now
    { id: 'mobile-money', label: tSync('Mobile Money'), icon: '📱' }
  ]

  const renderMobileInterface = () => (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Status Bar */}
      <div className="bg-black text-white text-xs px-4 py-1 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <span className="font-medium">9:41</span>
        </div>
        <div className="flex items-center gap-2">
          {getSignalIcon()}
          <Wifi className="w-3 h-3" />
          {getBatteryIcon()}
        </div>
      </div>

      {/* Mobile Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
          <h1 className="text-lg font-semibold"><TranslatedText text="Banking" /></h1>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => setManualViewOverride(manualViewOverride === 'desktop' ? null : 'desktop')}
            title={tSync("Switch to Desktop View")}
          >
            <Monitor className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm">
            <Search className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm">
            <Bell className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm">
            <User className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="bg-white border-b border-gray-200 p-4">
          <div className="grid grid-cols-2 gap-3">
            {mobileTabs.map((tab) => (
              <Button
                key={tab.id}
                variant={activeTab === tab.id ? 'default' : 'outline'}
                className="flex items-center gap-2 justify-start"
                onClick={() => {
                  setActiveTab(tab.id)
                  setIsMobileMenuOpen(false)
                }}
              >
                <span className="text-lg">{tab.icon}</span>
                <span className="text-sm">{tab.label}</span>
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Mobile Content */}
      <div className="pb-20">
        {activeTab === 'dashboard' && (
          <div className="p-4 space-y-4">
            <BankingDashboard companyId={companyId} />
          </div>
        )}
        {activeTab === 'transactions' && (
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold"><TranslatedText text="Recent Transactions" /></h2>
              <div className="flex gap-2">
                <Button 
                  onClick={() => setIsAddTransactionOpen && setIsAddTransactionOpen(true)}
                  size="sm"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  <TranslatedText text="Add Transaction" />
                </Button>
                <Button 
                  onClick={() => setAccountsRefreshKey && setAccountsRefreshKey((k) => k + 1)}
                  variant="outline"
                  size="sm"
                >
                  <TranslatedText text="Refresh" />
                </Button>
              </div>
            </div>
            <TransactionsList />
          </div>
        )}
        {/* {activeTab === 'ai' && (
          <div className="p-4">
            <AICategorization companyId={companyId} />
          </div>
        )} */}
        {activeTab === 'currency' && (
          <div className="p-4">
            <MultiCurrencyConverter />
          </div>
        )}
        {/* {activeTab === 'payments' && (
          <div className="p-4">
            <PaymentProcessorManager companyId={companyId} />
          </div>
        )} */}
        {activeTab === 'analytics' && (
          <div className="p-4">
            <AdvancedAnalytics />
          </div>
        )}
        {/* {activeTab === 'connections' && (
          <div className="p-4">
            <BankConnectionManager companyId={companyId} />
          </div>
        )} */}
        {activeTab === 'mobile-money' && (
          <div className="p-4">
            <MobileMoneyManager companyId={companyId} />
          </div>
        )}
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200">
        <div className="flex items-center justify-around py-2">
          {mobileTabs.slice(0, 5).map((tab) => (
            <Button
              key={tab.id}
              variant="ghost"
              size="sm"
              className={`flex flex-col items-center gap-1 ${
                activeTab === tab.id ? 'text-blue-600' : 'text-gray-600'
              }`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="text-lg">{tab.icon}</span>
              <span className="text-xs">{tab.label}</span>
            </Button>
          ))}
        </div>
      </div>
    </div>
  )

  // Banking Components - Memoized to prevent recreation
  const AccountCards = useMemo(() => {
    const Component = () => {
      const [accounts, setAccounts] = useState<BankAccount[] | null>(null)
      const [loading, setLoading] = useState(true)
      const [error, setError] = useState<string | null>(null)

      const fetchAccounts = async () => {
        console.log('🔄 fetchAccounts called', { 
          isAuthenticated, 
          authLoading, 
          companyId,
          timestamp: new Date().toISOString()
        })
        
        if (!isAuthenticated || authLoading || !companyId) {
          console.log('❌ fetchAccounts blocked:', { 
            hasCompanyId: !!companyId, 
            isAuthenticated, 
            authLoading 
          })
          return
        }
        
        let isMounted = true
        setLoading(true)
        
        try {
          console.log('📡 Fetching accounts...')
          const rows = await bankingApi.getBankAccounts(companyId)
          if (!isMounted) return
          console.log('✅ Accounts fetched:', rows.length)
          setAccounts(rows)
          setError(null)
        } catch (err: any) {
          if (!isMounted) return
          const errorMessage = err.message || String(err)
          console.error('❌ Error fetching accounts:', errorMessage)
          setError(errorMessage)
        } finally {
          if (isMounted) setLoading(false)
        }
      }

      useEffect(() => {
        fetchAccounts()
      }, [accountsRefreshKey, companyId, isAuthenticated, authLoading])

    if (loading) return (
      <>
        <Card><CardContent className="p-4"><TranslatedText text="Loading accounts..." /></CardContent></Card>
        <Card><CardContent className="p-4">&nbsp;</CardContent></Card>
        <Card><CardContent className="p-4">&nbsp;</CardContent></Card>
      </>
    )

    if (error) return <Card><CardContent className="p-4"><TranslatedText text="Error" />: {error || <TranslatedText text="Unknown error" />}</CardContent></Card>

    if (!accounts || accounts.length === 0) return (
      <Card><CardContent className="p-4"><TranslatedText text="No connected accounts" /></CardContent></Card>
    )

    return (
      <>
        {accounts.map((account) => (
          <Card key={account.id}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{account.bankName}</p>
                  <p className="text-xl font-bold">
                    {(() => {
                      const balance = typeof account.balance === 'number' 
                        ? account.balance 
                        : typeof account.balance === 'string' 
                        ? parseFloat(account.balance) 
                        : 0;
                      const formatted = balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                      return `${formatted} Frw`;
                    })()}
                  </p>
                  <p className="text-xs text-muted-foreground">{account.accountNumber} • {account.accountType || 'checking'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </>
    )
    }
    return Component
  }, [companyId, isAuthenticated, authLoading, accountsRefreshKey])

  const TransactionsList = useMemo(() => {
    const Component = () => {
    // All hooks must be at the top level - before any conditional returns
    const [txns, setTxns] = useState<BankTransaction[] | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [totalTransactions, setTotalTransactions] = useState(0)
    const [pageSize, setPageSize] = useState(20) // Configurable page size (default: 20)
    
    // Filter state
    const [selectedBankAccount, setSelectedBankAccount] = useState<string>('all')
    const [selectedStatus, setSelectedStatus] = useState<string>('all')
    const [searchTerm, setSearchTerm] = useState<string>('')
    const [dateFrom, setDateFrom] = useState<string>('')
    const [dateTo, setDateTo] = useState<string>('')
    const [showFilters, setShowFilters] = useState(false)
    
    // Bank accounts for filter dropdown
    const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([])
    const [loadingAccounts, setLoadingAccounts] = useState(false)
    
    // Reconciliation loading states
    const [reconcilingTransactionId, setReconcilingTransactionId] = useState<string | null>(null)
    const [autoReconcilingTransactionId, setAutoReconcilingTransactionId] = useState<string | null>(null)
    const [autoReconcilingAll, setAutoReconcilingAll] = useState(false)
    
    // Edit transaction dialog state
    const [isEditTransactionOpen, setIsEditTransactionOpen] = useState(false)
    const [editingTransaction, setEditingTransaction] = useState<BankTransaction | null>(null)
    const [editForm, setEditForm] = useState({
      description: '',
      amount: '',
      transactionType: 'debit' as 'credit' | 'debit',
      transactionDate: '',
      merchantName: '',
      category: '',
      reference: '',
      memo: ''
    })
    const [isSavingEdit, setIsSavingEdit] = useState(false)
    const [manualMode, setManualMode] = useState(false) // If true, disable auto-categorization
    
    // Notification state (replaces toast)
    const [notification, setNotification] = useState<{
      title: string
      message: string
      type: 'success' | 'error' | 'warning' | 'info'
    } | null>(null)

    // Fetch bank accounts for filter
    const fetchBankAccounts = async () => {
      if (!companyId) return
      setLoadingAccounts(true)
      try {
        const accounts = await bankingApi.getBankAccounts(companyId)
        setBankAccounts(accounts || [])
      } catch (err) {
        console.error('Error fetching bank accounts:', err)
      } finally {
        setLoadingAccounts(false)
      }
    }
    
    const fetchTransactions = async (page = currentPage) => {
      console.log('🔄 fetchTransactions called', { 
        isAuthenticated, 
        authLoading, 
        companyId,
        page,
        pageSize,
        selectedBankAccount,
        selectedStatus,
        searchTerm,
        dateFrom,
        dateTo,
        timestamp: new Date().toISOString()
      })
      
      if (!companyId || !isAuthenticated || authLoading) {
        console.log('❌ fetchTransactions blocked:', { 
          hasCompanyId: !!companyId, 
          isAuthenticated, 
          authLoading 
        })
        return
      }
      
      let isMounted = true
      setLoading(true)
      
      try {
        console.log('📡 Fetching transactions...')
        // Use selectedBankAccount if set, otherwise undefined (will use companyId)
        const bankAccountId = selectedBankAccount && selectedBankAccount !== 'all' ? selectedBankAccount : undefined
        const status = selectedStatus && selectedStatus !== 'all' ? selectedStatus : undefined
        const response = await bankingApi.getBankTransactions(bankAccountId, companyId, status, page, pageSize)
        
        if (!isMounted) return
        
        if (response && response.items) {
          let filteredItems = response.items
          
          // Apply client-side filters (search term, date range)
          if (searchTerm) {
            const searchLower = searchTerm.toLowerCase()
            filteredItems = filteredItems.filter(t => 
              (t.description || '').toLowerCase().includes(searchLower) ||
              (t.reference || '').toLowerCase().includes(searchLower) ||
              (t.merchantName || '').toLowerCase().includes(searchLower) ||
              (t.bankAccount?.bankName || '').toLowerCase().includes(searchLower)
            )
          }
          
          if (dateFrom) {
            const fromDate = new Date(dateFrom)
            filteredItems = filteredItems.filter(t => new Date(t.transactionDate) >= fromDate)
          }
          
          if (dateTo) {
            const toDate = new Date(dateTo)
            toDate.setHours(23, 59, 59, 999) // End of day
            filteredItems = filteredItems.filter(t => new Date(t.transactionDate) <= toDate)
          }
          
          console.log('✅ Transactions fetched:', filteredItems.length, 'of', response.total)
          setTxns(filteredItems)
          setTotalPages(response.totalPages || 1)
          setTotalTransactions(response.total || 0)
        } else {
          console.log('✅ No transactions found')
          setTxns([])
          setTotalPages(1)
          setTotalTransactions(0)
        }
        setError(null)
      } catch (err: any) {
        if (!isMounted) return
        const errorMessage = err.message || String(err)
        console.error('❌ Error fetching transactions:', errorMessage)
        setError(errorMessage)
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    useEffect(() => {
      if (companyId) {
        fetchBankAccounts()
      }
    }, [companyId])
    
    useEffect(() => {
      setCurrentPage(1)
      fetchTransactions(1) // Always start from page 1 when filters or companyId changes
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, isAuthenticated, authLoading, selectedBankAccount, selectedStatus, dateFrom, dateTo, accountsRefreshKey])
    
    // Debounce search term
    useEffect(() => {
      const timer = setTimeout(() => {
        setCurrentPage(1)
        fetchTransactions(1)
      }, 500) // Wait 500ms after user stops typing
      
      return () => clearTimeout(timer)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm])

    if (loading) return (
      <div className="flex items-center justify-center py-12">
        <MoonLoader size="md" color="teal" />
        <span className="ml-3 text-muted-foreground"><TranslatedText text="Loading transactions..." /></span>
      </div>
    )
    if (error) return <div className="space-y-4"><TranslatedText text="Error loading transactions" />: {error || <TranslatedText text="Unknown error" />}</div>
    if (!txns || txns.length === 0) {
      return (
        <div className="space-y-4">
          <p><TranslatedText text="No transactions found" /></p>
          <div className="flex gap-2 mt-2">
            <Button onClick={() => fetchTransactions()}><TranslatedText text="Retry" /></Button>
          </div>
        </div>
      )
    }

    const handleReconcile = async (transactionId: string) => {
      const transaction = txns?.find(t => t.id === transactionId)
      const transactionRef = transaction?.reference || transactionId.substring(0, 8)
      const transactionAmount = transaction?.amount ? Math.abs(Number(transaction.amount)).toLocaleString() : 'N/A'
      
      setReconcilingTransactionId(transactionId)
      
      // Show initial processing notification
      setNotification({
        title: '🔄 Processing Reconciliation...',
        message: `Reconciling transaction ${transactionRef} (${transactionAmount} ${transaction?.currency || 'RWF'})...`,
        type: 'info'
      })
      
      try {
        console.log('🔄 Reconciling transaction:', transactionId)
        console.log('   Company ID:', companyId)
        console.log('   Waiting for server response...')
        
        const result = await bankingApi.reconcileTransaction(transactionId, {})
        
        console.log('✅ Reconciliation result received:')
        console.log('   Result type:', typeof result)
        console.log('   Result:', result)
        console.log('   Result keys:', result ? Object.keys(result) : 'null/undefined')
        
        // Refresh transactions to get latest data
        await fetchTransactions(currentPage)
        
        // Check if result exists
        if (!result || typeof result !== 'object') {
          console.error('❌ Invalid result:', result)
          setNotification({
            title: '⚠️ Reconciliation Warning',
            message: `Transaction ${transactionRef} was reconciled but no details were returned.`,
            type: 'warning'
          })
          toast({
            title: tSync("⚠️ Reconciliation Warning"),
            description: tSync("Transaction was reconciled but no details were returned."),
            variant: "default"
          })
          return
        }
        
        // Check if reconciliation failed
        if (result.status === 'failed') {
          console.log('   ⚠️  Reconciliation failed - no matches found')
          // Update the transaction in local state to failed
          if (txns) {
            setTxns(txns.map(t => 
              t.id === transactionId 
                ? { ...t, status: 'failed', isReconciled: false }
                : t
            ))
          }
          
          setNotification({
            title: '⚠️ Reconciliation Failed',
            message: `No matching payment found for transaction ${transactionRef}.\n\nTransaction marked as 'Reconciled but not matched'.`,
            type: 'warning'
          })
          
          toast({
            title: tSync("⚠️ Reconciliation Failed"),
            description: result.message || tSync("No matching payment found. Transaction marked as 'Reconciled but not matched'."),
            variant: "destructive",
            duration: 5000
          })
        } else if (result.effects?.alreadyReconciled) {
          // Update the transaction in local state
          if (txns) {
            setTxns(txns.map(t => 
              t.id === transactionId 
                ? { ...t, status: result.status || 'reconciled', isReconciled: result.isReconciled || true, reconciledAt: result.reconciledAt }
                : t
            ))
          }
          console.log('   ℹ️  Transaction already reconciled')
          
          setNotification({
            title: 'ℹ️ Already Reconciled',
            message: `Transaction ${transactionRef} is already reconciled. No action needed.`,
            type: 'info'
          })
          
          toast({
            title: tSync("ℹ️ Already Reconciled"),
            description: tSync("This transaction was already reconciled."),
            variant: "default"
          })
        } else {
          // Update the transaction in local state
          if (txns) {
            setTxns(txns.map(t => 
              t.id === transactionId 
                ? { ...t, status: result.status || 'reconciled', isReconciled: result.isReconciled || true, reconciledAt: result.reconciledAt }
                : t
            ))
          }
          
          // Build detailed description based on effects
          let message = `Transaction ${transactionRef} has been successfully reconciled.`
          const details: string[] = []
          
          if (result.effects) {
            const effects = result.effects as any
            if (effects.paymentLinked) {
              details.push('✓ Payment linked to bank transaction')
            }
            if (effects.invoiceOrBillUpdated) {
              details.push('✓ Invoice/Bill status updated (paid or partially paid)')
            }
            if (!effects.paymentLinked && !effects.invoiceOrBillUpdated) {
              details.push('ℹ️ No payment or invoice/bill was linked')
            }
          } else if (result.matchedTransactionId) {
            details.push('✓ Transaction linked to accounting record')
          } else {
            details.push('ℹ️ Transaction marked as reconciled manually (no automatic matching)')
          }
          
          if (details.length > 0) {
            message += '\n\n' + details.join('\n')
          }
          
          console.log('   📝 Showing notification:', message)
          
          setNotification({
            title: '✅ Reconciliation Successful',
            message: message,
            type: 'success'
          })
          
          // Show toast notification
          toast({
            title: tSync("✅ Transaction Reconciled"),
            description: message.split('\n\n')[0],
            variant: "default",
            duration: 5000
          })
          
          console.log('   ✅ Notification sent')
        }
      } catch (error: any) {
        console.error('❌ Reconciliation error:', error)
        const errorMessage = error.response?.data?.message || error.message || 'Failed to reconcile transaction'
        
        setNotification({
          title: '❌ Reconciliation Failed',
          message: `Failed to reconcile transaction ${transactionRef}:\n${errorMessage}`,
          type: 'error'
        })
        
        toast({
          title: tSync("❌ Reconciliation Failed"),
          description: errorMessage,
          variant: "destructive",
          duration: 5000
        })
      } finally {
        setReconcilingTransactionId(null)
      }
    }

    const handleEditTransaction = async (transactionId: string) => {
      // Open edit dialog for transaction
      const transaction = txns?.find(t => t.id === transactionId)
      if (!transaction) {
        setNotification({
          title: 'Error',
          message: 'Transaction not found',
          type: 'error'
        })
        return
      }
      
      // Set editing transaction and populate form
      setEditingTransaction(transaction)
      setManualMode(false) // Regular edit mode
      // Handle transactionType: convert 'transfer' to 'debit' for edit form
      const editTransactionType = transaction.transactionType === 'transfer' 
        ? 'debit' 
        : (transaction.transactionType === 'credit' ? 'credit' : 'debit')
      setEditForm({
        description: transaction.description || '',
        amount: Math.abs(Number(transaction.amount)).toString(),
        transactionType: editTransactionType,
        transactionDate: transaction.transactionDate ? new Date(transaction.transactionDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        merchantName: transaction.merchantName || '',
        category: transaction.category || '',
        reference: transaction.reference || '',
        memo: transaction.memo || ''
      })
      setIsEditTransactionOpen(true)
    }

    const handleDeleteTransaction = async (transactionId: string) => {
      const transaction = txns?.find(t => t.id === transactionId)
      if (!transaction) return
      
      // Confirm deletion
      if (!confirm(tSync('Are you sure you want to delete this transaction? This action cannot be undone.'))) {
        return
      }
      
      try {
        await bankingApi.deleteBankTransaction(transactionId)
        
        // Refresh transactions
        await fetchTransactions(currentPage)
        
        setNotification({
          title: 'Success',
          message: 'Transaction deleted successfully',
          type: 'success'
        })
      } catch (error: any) {
        setNotification({
          title: 'Error',
          message: error.message || 'Failed to delete transaction',
          type: 'error'
        })
      }
    }

    const handleSaveEdit = async () => {
      if (!editingTransaction) return
      
      setIsSavingEdit(true)
      try {
        const updateData: any = {
          description: editForm.description,
          amount: editForm.transactionType === 'credit' ? Number(editForm.amount) : -Number(editForm.amount),
          transactionType: editForm.transactionType,
          transactionDate: editForm.transactionDate,
          merchantName: editForm.merchantName || null,
          category: editForm.category || null,
          reference: editForm.reference || null,
          memo: editForm.memo || null
        }
        
        // If not manual mode, trigger auto-categorization
        if (!manualMode) {
          // Auto-categorization will be handled by backend
        }
        
        await bankingApi.updateBankTransaction(editingTransaction.id, updateData)
        
        // Refresh transactions
        await fetchTransactions(currentPage)
        
        setIsEditTransactionOpen(false)
        setEditingTransaction(null)
        
        setNotification({
          title: 'Success',
          message: 'Transaction updated successfully',
          type: 'success'
        })
      } catch (error: any) {
        setNotification({
          title: 'Error',
          message: error.message || 'Failed to update transaction',
          type: 'error'
        })
      } finally {
        setIsSavingEdit(false)
      }
    }

    const handleAutoReconcile = async (transactionId: string) => {
      const transaction = txns?.find(t => t.id === transactionId)
      const transactionRef = transaction?.reference || transactionId.substring(0, 8)
      const transactionAmount = transaction?.amount ? Math.abs(Number(transaction.amount)).toLocaleString() : 'N/A'
      
      setAutoReconcilingTransactionId(transactionId)
      
      // Show initial processing notification
      setNotification({
        title: '🔍 Searching for Matches...',
        message: `Analyzing transaction ${transactionRef} (${transactionAmount} ${transaction?.currency || 'RWF'}) to find matching records...`,
        type: 'info'
      })
      
      try {
        // First, find matches to show what's available
        console.log('🔍 Finding matches for transaction:', transactionId)
        let matchResult: any = null
        try {
          matchResult = await bankingApi.findReconciliationMatches(transactionId, companyId)
          console.log('📊 Available matches:', matchResult)
        } catch (matchError: any) {
          console.warn('⚠️ Error finding matches, proceeding with auto-reconciliation:', matchError)
          // Continue with auto-reconciliation even if match finding fails
        }
        
        // Show match analysis notification (only if matchResult is valid)
        if (matchResult && matchResult.matches && Array.isArray(matchResult.matches) && matchResult.matches.length > 0) {
          const matchCount = matchResult.matches.length
          const bestMatch = matchResult.matches[0]
          const confidence = bestMatch?.confidence ? (bestMatch.confidence * 100).toFixed(0) : 'N/A'
          const reason = bestMatch?.reason || 'Match found'
          
          setNotification({
            title: `📊 Found ${matchCount} Potential Match${matchCount > 1 ? 'es' : ''}`,
            message: `Best match: ${confidence}% confidence - ${reason}\n\nAttempting auto-reconciliation...`,
            type: 'info'
          })
        } else {
          setNotification({
            title: '⚠️ No Matches Found',
            message: `No matching transactions found for ${transactionRef}.\n\nAttempting to reconcile anyway...`,
            type: 'warning'
          })
        }
        
        // Perform auto-reconciliation
        const result = await bankingApi.autoReconcileTransaction(transactionId, companyId)
        console.log('✅ Auto-reconciliation result:', result)
        
        // Update the transaction in local state
        if (result && result.success && txns) {
          setTxns(txns.map(t => 
            t.id === transactionId 
              ? { ...t, status: 'reconciled', isReconciled: true, reconciledAt: new Date().toISOString() }
              : t
          ))
        } else if (result && !result.success && txns) {
          // Mark as failed if no match found
          setTxns(txns.map(t => 
            t.id === transactionId 
              ? { ...t, status: 'failed' }
              : t
          ))
        }
        
        // Refresh transactions to get latest data
        await fetchTransactions(currentPage)
        
        if (result && result.success) {
          const confidence = result.match?.confidence ? (result.match.confidence * 100).toFixed(0) : 'N/A'
          let message = `Transaction ${transactionRef} has been auto-reconciled successfully.`
          const details: string[] = []
          
          if (result.match) {
            details.push(`✓ Matched with ${confidence}% confidence`)
            if (result.match.reason) {
              details.push(`  Reason: ${result.match.reason}`)
            }
          }
          
          if (result.match?.paymentId) {
            details.push('✓ Payment linked to bank transaction')
            details.push('✓ Invoice/Bill status updated (if applicable)')
          }
          
          if (details.length > 0) {
            message += '\n\n' + details.join('\n')
          }
          
          setNotification({
            title: '✅ Auto-Reconciliation Successful',
            message: message,
            type: 'success'
          })
          
          toast({
            title: tSync("✅ Auto-Reconciliation Successful"),
            description: message.split('\n\n')[0],
            variant: "default",
            duration: 5000
          })
        } else {
          // Build detailed match information for UI (with proper null checks)
          let matchDetails = ''
          if (matchResult && matchResult.matches && Array.isArray(matchResult.matches) && matchResult.matches.length > 0) {
            matchDetails = `Found ${matchResult.matches.length} potential match(es), but none met the confidence threshold:\n`
            matchResult.matches.forEach((match: any, index: number) => {
              const confidence = match?.confidence ? (match.confidence * 100).toFixed(0) : 'N/A'
              const reason = match?.reason || 'No reason provided'
              const matchInfo = `• Match ${index + 1}: ${confidence}% confidence - ${reason}`
              matchDetails += `\n${matchInfo}`
            })
          } else {
            matchDetails = 'No matching transactions found in the system.'
            if (matchResult && matchResult.availablePayments && Array.isArray(matchResult.availablePayments) && matchResult.availablePayments.length > 0) {
              matchDetails += `\n\nAvailable payments in system: ${matchResult.availablePayments.length}`
            }
          }
          
          let message = `No match found for transaction ${transactionRef}.`
          if (matchDetails) {
            message += `\n\n${matchDetails}`
          }
          message += '\n\nPlease reconcile manually using the "Reconciliate" button.'
          
          setNotification({
            title: '⚠️ No Match Found',
            message: message,
            type: 'warning'
          })
          
          toast({
            title: tSync("⚠️ No Match Found"),
            description: `No matching transaction found for ${transactionRef}. Please reconcile manually.`,
            variant: "destructive",
            duration: 5000
          })
        }
      } catch (error: any) {
        console.error('❌ Auto-reconciliation error:', error)
        const errorMessage = error.response?.data?.message || error.message || 'Failed to auto-reconcile transaction'
        
        setNotification({
          title: '❌ Auto-Reconciliation Error',
          message: `An error occurred while auto-reconciling transaction ${transactionRef}:\n${errorMessage}`,
          type: 'error'
        })
        
        toast({
          title: tSync("❌ Auto-Reconciliation Error"),
          description: errorMessage,
          variant: "destructive",
          duration: 5000
        })
      } finally {
        setAutoReconcilingTransactionId(null)
      }
    }

    const handleAutoReconcileAll = async () => {
      setAutoReconcilingAll(true)
      try {
        setNotification({
          title: tSync("🔄 Auto-Reconciliation Started"),
          message: tSync("Processing all unreconciled transactions. This may take a moment..."),
          type: 'info'
        })
        
        console.log('🔄 Starting auto-reconcile-all for companyId:', companyId)
        const result = await bankingApi.autoReconcileAll(companyId)
        console.log('✅ Auto-reconcile-all result:', result)
        console.log('   Result keys:', result ? Object.keys(result) : 'null/undefined')
        console.log('   Result.reconciled:', result?.reconciled)
        console.log('   Result.processed:', result?.processed)
        console.log('   Result.failed:', result?.failed)
        console.log('   Result.results:', result?.results)
        
        // Handle response - check both result structure and success flag
        const reconciled = result?.reconciled ?? result?.success?.reconciled ?? 0
        const processed = result?.processed ?? result?.success?.processed ?? 0
        const failed = result?.failed ?? result?.success?.failed ?? 0
        
        console.log('📊 Reconciliation stats (extracted):', { reconciled, processed, failed })
        
        // Update transactions in local state based on result
        if (txns && result?.results && Array.isArray(result.results)) {
          console.log('   🔄 Updating transaction statuses in UI...')
          console.log('   Results array length:', result.results.length)
          
          let updatedTxns = [...txns]
          
          // Update all transactions from results array
          result.results.forEach((r: any) => {
            const index = updatedTxns.findIndex(t => t.id === r.transactionId)
            if (index !== -1) {
              if (r.success) {
                // Reconciled successfully
                updatedTxns[index] = {
                  ...updatedTxns[index],
                  status: 'reconciled',
                  isReconciled: true,
                  reconciledAt: new Date().toISOString()
                }
                console.log(`   ✅ Updated transaction ${r.transactionId} to reconciled`)
              } else {
                // Failed to reconcile
                updatedTxns[index] = {
                  ...updatedTxns[index],
                  status: 'failed',
                  isReconciled: false
                }
                console.log(`   ⚠️  Updated transaction ${r.transactionId} to failed`)
              }
            } else {
              console.log(`   ⚠️  Transaction ${r.transactionId} not found in current list`)
            }
          })
          
          setTxns(updatedTxns)
          console.log('   ✅ Transaction statuses updated in UI')
        } else {
          console.log('   ⚠️  Cannot update transactions - missing results array or txns')
          console.log('   Has txns:', !!txns)
          console.log('   Has results:', !!result?.results)
          console.log('   Results is array:', Array.isArray(result?.results))
        }
        
        if (reconciled > 0) {
          setNotification({
            title: tSync("✅ Auto-Reconciliation Complete"),
            message: tSync("Successfully reconciled") + ` ${reconciled} ` + tSync("of") + ` ${processed} ` + tSync("transaction") + `${processed !== 1 ? 's' : ''}. ${failed > 0 ? `${failed} ` + tSync("failed.") : tSync('All successful!')}`,
            type: 'success'
          })
        } else if (processed > 0) {
          // Transactions were processed but none were reconciled (all failed)
          let message = tSync("Processed") + ` ${processed} ` + tSync("transaction") + `${processed !== 1 ? 's' : ''} ` + tSync("but none could be automatically matched.")
          
          if (failed > 0) {
            message += `\n\n` + tSync("All") + ` ${failed} ` + tSync("transaction") + `${failed !== 1 ? 's' : ''} ` + tSync("failed to reconcile. Reasons may include:") + `\n• ` + tSync("No matching payments/invoices found in the system") + `\n• ` + tSync("Confidence scores too low (< 80%)") + `\n• ` + tSync("Transactions not yet recorded in accounting") + `\n• ` + tSync("Bank fees, interest, or transfers") + `\n\n` + tSync("You can manually reconcile transactions using the 'Reconcile' button.")
          } else {
            message += `\n\n` + tSync("Possible reasons:") + `\n• ` + tSync("No matching payments or invoices in the system") + `\n• ` + tSync("Transactions are new and not yet recorded") + `\n• ` + tSync("Bank fees, interest, or transfers") + `\n\n` + tSync("You can manually reconcile transactions using the 'Reconcile' button.")
          }
          
          setNotification({
            title: tSync("⚠️ No Transactions Reconciled"),
            message: message,
            type: 'warning'
          })
        } else if (processed === 0 && failed === 0 && reconciled === 0) {
          // No transactions were processed at all
          const unreconciledCount = txns ? txns.filter(t => t.status !== 'reconciled' && !t.isReconciled && t.status !== 'failed').length : 0
          
          if (unreconciledCount > 0) {
            // There are unreconciled transactions but none were processed
            setNotification({
              title: tSync("⚠️ Auto-Reconciliation Skipped"),
              message: tSync("Found") + ` ${unreconciledCount} ` + tSync("unreconciled transaction") + `${unreconciledCount !== 1 ? 's' : ''}, ` + tSync("but none were processed. This may be due to:") + `\n• ` + tSync("No matching payments or invoices in the system") + `\n• ` + tSync("Transactions are new and not yet recorded") + `\n• ` + tSync("Bank fees, interest, or transfers") + `\n\n` + tSync("You can manually reconcile transactions using the 'Reconcile' button."),
              type: 'warning'
            })
          } else {
            // No unreconciled transactions at all
            setNotification({
              title: tSync("ℹ️ No Transactions to Reconcile"),
              message: tSync("All transactions are already reconciled or there are no unreconciled transactions."),
              type: 'info'
            })
          }
        }
      } catch (error: any) {
        console.error('❌ Auto-reconcile-all error:', error)
        setNotification({
          title: tSync("❌ Auto-Reconciliation Failed"),
          message: error.response?.data?.message || error.message || tSync("Failed to auto-reconcile transactions. Please try again."),
          type: 'error'
        })
      } finally {
        setAutoReconcilingAll(false)
      }
    }

    const unreconciledCount = txns.filter(t => t.status !== 'reconciled' && !t.isReconciled && t.status !== 'failed').length
    const failedCount = txns.filter(t => t.status === 'failed').length

    // Notification component
    const NotificationBanner = () => {
      if (!notification) return null
      
      const bgColor = {
        success: 'bg-green-50 border-green-200 text-green-900',
        error: 'bg-red-50 border-red-200 text-red-900',
        warning: 'bg-yellow-50 border-yellow-200 text-yellow-900',
        info: 'bg-blue-50 border-blue-200 text-blue-900'
      }[notification.type]
      
      const iconColor = {
        success: 'text-green-600',
        error: 'text-red-600',
        warning: 'text-yellow-600',
        info: 'text-blue-600'
      }[notification.type]
      
      const Icon = {
        success: CheckCircle2,
        error: AlertCircle,
        warning: AlertCircle,
        info: AlertCircle
      }[notification.type]
      
      return (
        <div className={`p-4 border rounded-lg ${bgColor} mb-4`}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 flex-1">
              <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${iconColor}`} />
              <div className="flex-1">
                <h4 className="font-semibold mb-1">{notification.title}</h4>
                <p className="text-sm whitespace-pre-line">{notification.message}</p>
              </div>
            </div>
            <button
              onClick={() => setNotification(null)}
              className="text-gray-500 hover:text-gray-700 flex-shrink-0"
              aria-label={tSync("Close notification")}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )
    }

    return (
      <div className="space-y-4">
        {/* Notification Banner */}
        <NotificationBanner />
        
        {/* Filters Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-600" />
              <h3 className="font-semibold text-gray-900"><TranslatedText text="Filters" /></h3>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                {showFilters ? (
                  <>
                    <X className="w-4 h-4 mr-1" />
                    <TranslatedText text="Hide" />
                  </>
                ) : (
                  <>
                    <Filter className="w-4 h-4 mr-1" />
                    <TranslatedText text="Show Filters" />
                  </>
                )}
              </Button>
              {(selectedBankAccount !== 'all' || selectedStatus !== 'all' || searchTerm || dateFrom || dateTo) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedBankAccount('all')
                    setSelectedStatus('all')
                    setSearchTerm('')
                    setDateFrom('')
                    setDateTo('')
                    setCurrentPage(1)
                  }}
                >
                  <X className="w-4 h-4 mr-1" />
                  <TranslatedText text="Clear All" />
                </Button>
              )}
            </div>
          </div>
          
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 pt-4 border-t border-gray-200">
              {/* Search */}
              <div className="lg:col-span-2">
                <Label htmlFor="search" className="text-sm font-medium text-gray-700 mb-1 block"><TranslatedText text="Search" /></Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="search"
                    placeholder={tSync("Search description, reference, merchant...")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              {/* Bank Account Filter */}
              <div className="relative min-w-0 overflow-hidden">
                <Label htmlFor="bankAccount" className="text-sm font-medium text-gray-700 mb-1 block"><TranslatedText text="Bank Account" /></Label>
                <Select value={selectedBankAccount} onValueChange={setSelectedBankAccount}>
                  <SelectTrigger id="bankAccount" disabled={loadingAccounts} className="w-full truncate">
                    <SelectValue placeholder={tSync("All Accounts")} />
                  </SelectTrigger>
                  <SelectContent className="!z-[60]">
                    <SelectItem value="all"><TranslatedText text="All Accounts" /></SelectItem>
                    {bankAccounts.map(account => (
                      <SelectItem key={account.id} value={account.id}>
                        {account.bankName} - {account.accountNumber}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Status Filter */}
              <div className="relative min-w-0">
                <Label htmlFor="status" className="text-sm font-medium text-gray-700 mb-1 block"><TranslatedText text="Status" /></Label>
                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger id="status" className="w-full">
                    <SelectValue placeholder={tSync("All Statuses")} />
                  </SelectTrigger>
                  <SelectContent className="!z-[50]">
                    <SelectItem value="all"><TranslatedText text="All Statuses" /></SelectItem>
                    <SelectItem value="reconciled"><TranslatedText text="Reconciled" /></SelectItem>
                    <SelectItem value="unreconciled"><TranslatedText text="Unreconciled" /></SelectItem>
                    <SelectItem value="failed"><TranslatedText text="Failed" /></SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Date Range */}
              <div className="lg:col-span-2 grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="dateFrom" className="text-sm font-medium text-gray-700 mb-1 block"><TranslatedText text="From Date" /></Label>
                  <Input
                    id="dateFrom"
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="dateTo" className="text-sm font-medium text-gray-700 mb-1 block"><TranslatedText text="To Date" /></Label>
                  <Input
                    id="dateTo"
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Transaction Summary */}
        <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div>
            <p className="font-medium text-blue-900">
              <TranslatedText text="Showing" /> {txns.length} <TranslatedText text="of" /> {totalTransactions} <TranslatedText text="transaction" />{totalTransactions !== 1 ? 's' : ''}
              {totalPages > 1 && ` (` + tSync("Page") + ` ${currentPage} ` + tSync("of") + ` ${totalPages})`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Page Size Selector */}
            <div className="flex items-center gap-2">
              <Label htmlFor="pageSize" className="text-sm text-gray-600"><TranslatedText text="Show" />:</Label>
              <Select value={pageSize.toString()} onValueChange={(value) => {
                const newPageSize = parseInt(value)
                setPageSize(newPageSize)
                setCurrentPage(1)
                // Fetch transactions with new page size
                setTimeout(() => {
                  fetchTransactions(1)
                }, 0)
              }}>
                <SelectTrigger id="pageSize" className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                  <SelectItem value="200">200</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Auto-Reconcile All button hidden - reconciliation is now automatic when bank statements are uploaded */}
            {/* {unreconciledCount > 0 && (
              <Button 
                onClick={handleAutoReconcileAll}
                size="sm"
                disabled={autoReconcilingAll}
              >
                {autoReconcilingAll ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Auto-Reconcile All
                  </>
                )}
              </Button>
            )} */}
          </div>
        </div>

        {txns.map((transaction) => {
          const isReconciled = transaction.status === 'reconciled' || transaction.isReconciled
          const isFailed = transaction.status === 'failed'
          return (
            <div key={transaction.id} className={`flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 ${isFailed ? 'bg-orange-50 border-orange-200' : ''}`}>
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${transaction.transactionType === 'credit' ? 'bg-green-100' : 'bg-red-100'}`}>
                  {transaction.transactionType === 'credit' ? <span className="text-green-600 font-semibold">↑</span> : <span className="text-red-600 font-semibold">↓</span>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant={transaction.transactionType === 'credit' ? 'default' : 'destructive'} className="text-xs">
                      {transaction.transactionType === 'credit' ? tSync('CREDIT') : tSync('DEBIT')}
                    </Badge>
                    <p className="text-sm text-muted-foreground">
                      {(() => {
                        const date = new Date(transaction.transactionDate);
                        // Format date directly from UTC components to avoid timezone shifts
                        const day = String(date.getUTCDate()).padStart(2, '0');
                        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
                        const year = date.getUTCFullYear();
                        return `${day}/${month}/${year}`;
                      })()}
                    </p>
                  </div>
                  <p className="font-medium break-words mb-1">{transaction.description || transaction.memo || tSync('Bank transaction')}</p>
                  {transaction.memo && transaction.memo !== transaction.description && !transaction.memo.includes('[Categorization:') && (
                    <p className="text-sm text-muted-foreground break-words mb-1">{transaction.memo}</p>
                  )}
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {transaction.reference && (
                      <p className="text-xs text-muted-foreground">
                        <span className="font-semibold">{tSync('Ref:')}</span> {transaction.reference}
                      </p>
                    )}
                    {transaction.merchantName && (
                      <p className="text-xs text-muted-foreground">
                        <span className="font-semibold">{tSync('Merchant:')}</span> {transaction.merchantName}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className={`font-medium ${Number(transaction.amount) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(() => {
                      const amount = Number(transaction.amount);
                      const formatted = Math.abs(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                      return `${amount >= 0 ? '+' : '-'}${formatted} Frw`;
                    })()}
                  </p>
                  <div className="flex items-center gap-2">
                    {isReconciled ? (
                      <Badge variant="default" className="bg-green-100 text-green-800">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        <TranslatedText text="Reconciled" />
                      </Badge>
                    ) : isFailed ? (
                      <Badge variant="destructive" className="bg-orange-100 text-orange-800">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        <TranslatedText text="Reconciled but not matched" />
                      </Badge>
                    ) : (
                      <Badge variant="secondary"><TranslatedText text="Unreconciled" /></Badge>
                    )}
                  </div>
                </div>
                {/* RECONCILE BUTTON: Show for all unreconciled transactions */}
                {/* Both internal transactions (unreconciled) and bank statement transactions (needs_review) can be reconciled */}
                {!isReconciled && (
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      size="sm"
                      variant="default"
                      onClick={() => handleAutoReconcile(transaction.id)}
                      title="Reconcile transaction - match with internal records or bank statements"
                      disabled={autoReconcilingTransactionId === transaction.id || reconcilingTransactionId === transaction.id || autoReconcilingAll}
                    >
                      {autoReconcilingTransactionId === transaction.id ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          Matching...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-1" />
                          Reconciliate
                        </>
                      )}
                    </Button>
                  </div>
                )}
                {/* EDIT/DELETE BUTTONS: Show for all transactions that are not reconciled */}
                {!isReconciled && (
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEditTransaction(transaction.id)}
                      title="Edit transaction"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDeleteTransaction(transaction.id)}
                      title="Delete transaction"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )
        })}
        
        {/* Pagination Controls - At Bottom */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg mt-4">
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setCurrentPage(1)
                  fetchTransactions(1)
                }}
                disabled={currentPage === 1}
                title={tSync("First page")}
              >
                <ChevronsLeft className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const newPage = currentPage - 1
                  if (newPage >= 1) {
                    setCurrentPage(newPage)
                    fetchTransactions(newPage)
                  }
                }}
                disabled={currentPage === 1}
                title={tSync("Previous page")}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                <TranslatedText text="Previous" />
              </Button>
              <div className="flex items-center gap-2 px-3">
                <span className="text-sm text-gray-600">
                  <TranslatedText text="Page" />
                </span>
                <Input
                  type="number"
                  min={1}
                  max={totalPages}
                  value={currentPage}
                  onChange={(e) => {
                    const page = parseInt(e.target.value)
                    if (page >= 1 && page <= totalPages) {
                      setCurrentPage(page)
                      fetchTransactions(page)
                    }
                  }}
                  className="w-16 text-center"
                />
                <span className="text-sm text-gray-600">
                  <TranslatedText text="of" /> {totalPages}
                </span>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const newPage = currentPage + 1
                  if (newPage <= totalPages) {
                    setCurrentPage(newPage)
                    fetchTransactions(newPage)
                  }
                }}
                disabled={currentPage >= totalPages}
                title={tSync("Next page")}
              >
                <TranslatedText text="Next" />
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setCurrentPage(totalPages)
                  fetchTransactions(totalPages)
                }}
                disabled={currentPage >= totalPages}
                title={tSync("Last page")}
              >
                <ChevronsRight className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">
                {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalTransactions)} of {totalTransactions}
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  fetchTransactions(currentPage)
                }}
                title="Refresh current page"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
        
        {/* Edit Transaction Dialog - inside TransactionsList to access state */}
        <Dialog open={isEditTransactionOpen} onOpenChange={(open) => {
          setIsEditTransactionOpen(open)
          if (!open) {
            setEditingTransaction(null)
            setManualMode(false)
          }
        }}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                <TranslatedText text={manualMode ? "Edit Transaction (Manual)" : "Edit Transaction"} />
              </DialogTitle>
              <DialogDescription>
                <TranslatedText text={manualMode ? "Edit transaction manually without automatic categorization." : "Edit transaction with automatic categorization."} />
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-description"><TranslatedText text="Description" /></Label>
                <Input
                  id="edit-description"
                  value={editForm.description}
                  onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                  placeholder={tSync("Transaction description")}
                />
              </div>
              
              <div>
                <Label htmlFor="edit-amount"><TranslatedText text="Amount" /></Label>
                <Input
                  id="edit-amount"
                  type="number"
                  step="0.01"
                  value={editForm.amount}
                  onChange={(e) => setEditForm({...editForm, amount: e.target.value})}
                  placeholder={tSync("0.00")}
                />
              </div>
              
              <div>
                <Label htmlFor="edit-transactionType"><TranslatedText text="Type" /></Label>
                <Select
                  value={editForm.transactionType}
                  onValueChange={(value: 'credit' | 'debit') => setEditForm({...editForm, transactionType: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="credit"><TranslatedText text="Credit (Money In)" /></SelectItem>
                    <SelectItem value="debit"><TranslatedText text="Debit (Money Out)" /></SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="edit-transactionDate"><TranslatedText text="Date" /></Label>
                <Input
                  id="edit-transactionDate"
                  type="date"
                  value={editForm.transactionDate}
                  onChange={(e) => setEditForm({...editForm, transactionDate: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="edit-merchantName"><TranslatedText text="Merchant (Optional)" /></Label>
                <Input
                  id="edit-merchantName"
                  value={editForm.merchantName}
                  onChange={(e) => setEditForm({...editForm, merchantName: e.target.value})}
                  placeholder={tSync("Merchant name")}
                />
              </div>
              
              <div>
                <Label htmlFor="edit-category"><TranslatedText text="Category (Optional)" /></Label>
                <Input
                  id="edit-category"
                  value={editForm.category}
                  onChange={(e) => setEditForm({...editForm, category: e.target.value})}
                  placeholder={tSync("Transaction category")}
                />
              </div>
              
              <div>
                <Label htmlFor="edit-reference"><TranslatedText text="Reference (Optional)" /></Label>
                <Input
                  id="edit-reference"
                  value={editForm.reference}
                  onChange={(e) => setEditForm({...editForm, reference: e.target.value})}
                  placeholder={tSync("Reference number")}
                />
              </div>
              
              <div>
                <Label htmlFor="edit-memo"><TranslatedText text="Memo (Optional)" /></Label>
                <Input
                  id="edit-memo"
                  value={editForm.memo}
                  onChange={(e) => setEditForm({...editForm, memo: e.target.value})}
                  placeholder={tSync("Additional notes")}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditTransactionOpen(false)} disabled={isSavingEdit}>
                <TranslatedText text="Cancel" />
              </Button>
              <Button onClick={handleSaveEdit} disabled={isSavingEdit}>
                {isSavingEdit ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    <TranslatedText text="Saving..." />
                  </>
                ) : (
                  <TranslatedText text="Save Changes" />
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    )
    }
    return Component
  }, [companyId, isAuthenticated, authLoading])

  const renderTabletInterface = () => (
    <div className="min-h-screen bg-gray-50">
      {/* Tablet Header */}
      <div className="bg-white border-b border-gray-200 py-4">
        <div className="flex items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold">Banking Dashboard</h1>
          </div>
          {/* Settings and Profile buttons hidden for now */}
          {/* <div className="flex items-center gap-3">
            <Button variant="outline" size="sm">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
            <Button variant="outline" size="sm">
              <User className="w-4 h-4 mr-2" />
              Profile
            </Button>
          </div> */}
        </div>
      </div>

      {/* Tablet Content */}
      <div className="p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
            {/* <TabsTrigger value="ai">AI Features</TabsTrigger> */}
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
            {/* <TabsTrigger value="connections">Connections</TabsTrigger> */}
            <TabsTrigger value="mobile-money">Mobile Money</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="space-y-6">
            <BankingDashboard companyId={companyId} />
          </TabsContent>

          {/* <TabsContent value="ai" className="space-y-6">
            <AICategorization companyId={companyId} />
          </TabsContent> */}

          <TabsContent value="analytics" className="space-y-6">
            <AdvancedAnalytics companyId={companyId} />
          </TabsContent>

          {/* <TabsContent value="connections" className="space-y-6">
            <BankConnectionManager companyId={companyId} />
          </TabsContent> */}

          <TabsContent value="mobile-money" className="space-y-6">
            <MobileMoneyManager companyId={companyId} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )

  const renderDesktopInterface = () => (
    <div className="min-h-screen bg-gray-50 -mx-6">
      {/* Desktop Header */}
      <div className="bg-white border-b border-gray-200 py-2.5">
        <div className="flex items-center justify-between px-6">
          <h1 className="text-2xl font-semibold">Banking Management</h1>
          {/* Settings and Profile buttons hidden for now */}
          {/* <div className="flex items-center gap-4">
            <Button variant="outline">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
            <Button variant="outline">
              <User className="w-4 h-4 mr-2" />
              Profile
            </Button>
          </div> */}
        </div>
      </div>

      {/* Desktop Content */}
      <div className="pt-3 pb-8">
        <div className="px-6 pr-20">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="flex w-full mb-3 overflow-x-auto gap-1 p-0 justify-start">
            <TabsTrigger value="dashboard" className="whitespace-nowrap"><TranslatedText text="Dashboard" /></TabsTrigger>
            <TabsTrigger value="bank-management" className="whitespace-nowrap"><TranslatedText text="Bank Management" /></TabsTrigger>
            <TabsTrigger value="transactions" className="whitespace-nowrap"><TranslatedText text="Transactions" /></TabsTrigger>
            {/* <TabsTrigger value="ai" className="whitespace-nowrap">AI Categorization</TabsTrigger> */}
            <TabsTrigger value="currency" className="whitespace-nowrap"><TranslatedText text="Multi-Currency" /></TabsTrigger>
            {/* <TabsTrigger value="payments" className="whitespace-nowrap">Payment Processors</TabsTrigger> */}
            <TabsTrigger value="analytics" className="whitespace-nowrap"><TranslatedText text="Advanced Analytics" /></TabsTrigger>
            {/* <TabsTrigger value="connections" className="whitespace-nowrap">Bank Connections</TabsTrigger> */}
            <TabsTrigger value="mobile-money" className="whitespace-nowrap"><TranslatedText text="Mobile Money" /></TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="space-y-6">
            <div className="space-y-6">
              <BankingDashboard companyId={companyId} />
            </div>
          </TabsContent>

          <TabsContent value="bank-management" className="space-y-6">
            <BankManagement 
              companyId={companyId}
              onRefresh={() => setAccountsRefreshKey && setAccountsRefreshKey((k) => k + 1)}
            />
          </TabsContent>

          <TabsContent value="transactions" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold"><TranslatedText text="Recent Transactions" /></h2>
              <div className="flex gap-2">
                <Button 
                  onClick={() => setIsAddTransactionOpen && setIsAddTransactionOpen(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  <TranslatedText text="Add Transaction" />
                </Button>
                <Button 
                  onClick={() => setAccountsRefreshKey && setAccountsRefreshKey((k) => k + 1)}
                  variant="outline"
                >
                  <TranslatedText text="Refresh" />
                </Button>
              </div>
            </div>
            <TransactionsList />
          </TabsContent>

          {/* <TabsContent value="ai" className="space-y-6">
            <AICategorization companyId={companyId} />
          </TabsContent> */}

          <TabsContent value="currency" className="space-y-6">
            <CurrencyDashboard />
          </TabsContent>

          {/* <TabsContent value="payments" className="space-y-6">
            <PaymentProcessorManager companyId={companyId} />
          </TabsContent> */}

          <TabsContent value="analytics" className="space-y-6">
            <AdvancedAnalytics companyId={companyId} />
          </TabsContent>

          {/* <TabsContent value="connections" className="space-y-6">
            <BankConnectionManager companyId={companyId} />
          </TabsContent> */}

          <TabsContent value="mobile-money" className="space-y-6">
            <MobileMoneyManager companyId={companyId} />
          </TabsContent>
        </Tabs>
        </div>
      </div>
    </div>
  )

  // BankAccountSelect component for transaction dialog
  // BankAccountSelect component - moved outside useMemo to avoid hook issues
  const BankAccountSelect = () => {
    const [accounts, setAccounts] = useState<BankAccount[]>([])
    const [loading, setLoading] = useState(true)

    const fetchAccounts = async () => {
      if (!isAuthenticated || authLoading || !companyId) return
      
      try {
        const response = await bankingApi.getBankAccounts(companyId)
        setAccounts(response)
      } catch (error) {
        console.error('Error fetching bank accounts:', error)
      } finally {
        setLoading(false)
      }
    }

    useEffect(() => {
      fetchAccounts()
    }, [companyId, isAuthenticated, authLoading])

    if (loading) {
      return <SelectItem value="loading" disabled><TranslatedText text="Loading accounts..." /></SelectItem>
    }

    if (accounts.length === 0) {
      return <SelectItem value="no-accounts" disabled><TranslatedText text="No bank accounts found" /></SelectItem>
    }

    return (
      <>
        {accounts.map((account) => (
          <SelectItem key={account.id} value={account.id}>
            {account.bankName} - {account.accountNumber} ({account.accountType})
          </SelectItem>
        ))}
      </>
    )
  }

  // Edit Transaction Dialog Component is now defined inside TransactionsList Component

  // Stable handler to prevent dialog from closing on form changes
  const handleDialogOpenChange = useCallback((open: boolean) => {
    if (setIsAddTransactionOpen) {
      setIsAddTransactionOpen(open)
    }
  }, [setIsAddTransactionOpen])

  // Stable handler wrapper for form changes - memoized to prevent recreation
  const stableHandleTransactionFormChange = useCallback((field: string, value: string) => {
    if (handleTransactionFormChange) {
      handleTransactionFormChange(field, value)
    }
  }, [handleTransactionFormChange])

  // Transaction Dialog Component - Render directly without memoization to allow form updates
  // The dialog will re-render when form changes, but won't close because open state is stable
  // Use a key to ensure dialog stays mounted when form changes
  const TransactionDialog = (
    <Dialog 
      key="add-transaction-dialog"
      open={isAddTransactionOpen || false} 
      onOpenChange={handleDialogOpenChange}
    >
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pb-2">
          <DialogTitle className="text-lg"><TranslatedText text="Add Bank Transaction" /></DialogTitle>
          <DialogDescription className="text-xs"><TranslatedText text="Create a new bank transaction manually." /></DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <div className="space-y-1">
            <Label htmlFor="bankAccount" className="text-xs font-medium"><TranslatedText text="Bank Account" /></Label>
            <Select
              value={transactionForm?.bankAccountId || ''}
              onValueChange={(value) => stableHandleTransactionFormChange('bankAccountId', value)}
            >
              <SelectTrigger className="w-full border border-gray-200 h-9">
                <SelectValue placeholder={tSync("Select bank account...")} />
              </SelectTrigger>
              <SelectContent>
                <BankAccountSelect />
              </SelectContent>
            </Select>
            {transactionFormErrors?.bankAccountId && (
              <p className="text-xs text-destructive mt-0.5">{transactionFormErrors.bankAccountId}</p>
            )}
          </div>
          
          <div className="space-y-1">
            <Label htmlFor="description" className="text-xs font-medium"><TranslatedText text="Description" /></Label>
            <Input
              id="description"
              className="border border-gray-200 h-9"
              value={transactionForm?.description || ''}
              onChange={(e) => stableHandleTransactionFormChange('description', e.target.value)}
              placeholder={tSync("Transaction description")}
            />
            {transactionFormErrors?.description && (
              <p className="text-xs text-destructive mt-0.5">{transactionFormErrors.description}</p>
            )}
          </div>
          
          <div className="space-y-1">
            <Label htmlFor="amount" className="text-xs font-medium"><TranslatedText text="Amount" /></Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              className="border border-gray-200 h-9"
              value={transactionForm?.amount || ''}
              onChange={(e) => stableHandleTransactionFormChange('amount', e.target.value)}
              placeholder={tSync("0.00")}
            />
            {transactionFormErrors?.amount && (
              <p className="text-xs text-destructive mt-0.5">{transactionFormErrors.amount}</p>
            )}
          </div>
          
          <div className="space-y-1">
            <Label htmlFor="transactionType" className="text-xs font-medium"><TranslatedText text="Type" /></Label>
            <Select
              value={transactionForm?.transactionType || 'debit'}
              onValueChange={(value) => stableHandleTransactionFormChange('transactionType', value)}
            >
              <SelectTrigger className="w-full border border-gray-200 h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="credit"><TranslatedText text="Credit (Money In)" /></SelectItem>
                <SelectItem value="debit"><TranslatedText text="Debit (Money Out)" /></SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-1">
            <Label htmlFor="transactionDate" className="text-xs font-medium"><TranslatedText text="Date" /></Label>
            <Input
              id="transactionDate"
              type="date"
              className="border border-gray-200 h-9"
              value={transactionForm?.transactionDate || ''}
              onChange={(e) => stableHandleTransactionFormChange('transactionDate', e.target.value)}
            />
            {transactionFormErrors?.transactionDate && (
              <p className="text-xs text-destructive mt-0.5">{transactionFormErrors.transactionDate}</p>
            )}
          </div>
          
          <div className="space-y-1">
            <Label htmlFor="merchantName" className="text-xs font-medium"><TranslatedText text="Merchant (Optional)" /></Label>
            <Input
              id="merchantName"
              className="border border-gray-200 h-9"
              value={transactionForm?.merchantName || ''}
              onChange={(e) => stableHandleTransactionFormChange('merchantName', e.target.value)}
              placeholder={tSync("Merchant name")}
            />
          </div>
          
          <div className="space-y-1">
            <Label htmlFor="category" className="text-xs font-medium"><TranslatedText text="Category (Optional)" /></Label>
            <Input
              id="category"
              className="border border-gray-200 h-9"
              value={transactionForm?.category || ''}
              onChange={(e) => stableHandleTransactionFormChange('category', e.target.value)}
              placeholder={tSync("Transaction category")}
            />
          </div>
          
          <div className="space-y-1">
            <Label htmlFor="reference" className="text-xs font-medium"><TranslatedText text="Reference (Optional)" /></Label>
            <Input
              id="reference"
              className="border border-gray-200 h-9"
              value={transactionForm?.reference || ''}
              onChange={(e) => stableHandleTransactionFormChange('reference', e.target.value)}
              placeholder={tSync("Reference number")}
            />
          </div>
          
          <div className="space-y-1">
            <Label htmlFor="memo" className="text-xs font-medium"><TranslatedText text="Memo (Optional)" /></Label>
            <Input
              id="memo"
              className="border border-gray-200 h-9"
              value={transactionForm?.memo || ''}
              onChange={(e) => stableHandleTransactionFormChange('memo', e.target.value)}
              placeholder={tSync("Additional notes")}
            />
          </div>
          
          {transactionFormErrors?.submit && (
            <p className="text-xs text-destructive">{transactionFormErrors.submit}</p>
          )}
        </div>
        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={() => setIsAddTransactionOpen?.(false)}>
            <TranslatedText text="Cancel" />
          </Button>
          <Button onClick={handleCreateTransaction}>
            <TranslatedText text="Create Transaction" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )

  // Device-specific rendering
  switch (deviceType) {
    case 'mobile':
      return (
        <>
          {renderMobileInterface()}
          {TransactionDialog}
        </>
      )
    case 'tablet':
      return (
        <>
          {renderTabletInterface()}
          {TransactionDialog}
        </>
      )
    case 'desktop':
      return (
        <>
          {renderDesktopInterface()}
          {TransactionDialog}
        </>
      )
    default:
      return (
        <>
          {renderDesktopInterface()}
          {TransactionDialog}
        </>
      )
  }
}
